import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { CalendarClock, Calculator, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Logo } from '../brand/Logo';
import { Button } from '../ui/button';

const NAV_ITEMS = [
  { to: '/', label: 'Timesheet', icon: CalendarClock },
  { to: '/tax-calculator', label: 'Tax', icon: Calculator },
] as const;

const MainNav: React.FC<{ className?: string }> = ({ className }) => (
  <nav aria-label="Main" className={cn('flex items-center gap-1 rounded-xl border border-line bg-surface/70 p-1', className)}>
    {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        end
        className={({ isActive }) =>
          cn(
            'flex h-8 flex-1 items-center justify-center gap-2 rounded-lg px-3 text-[13px] font-medium transition-colors',
            isActive
              ? 'bg-elevated text-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.06),0_1px_2px_rgb(0_0_0/0.4)]'
              : 'text-fg-subtle hover:text-fg'
          )
        }
      >
        <Icon className="size-4" />
        {label}
      </NavLink>
    ))}
  </nav>
);

const UserMenu: React.FC = () => {
  const { user, signOut } = useAuth();
  const email = user?.email ?? '';
  const initial = email.charAt(0).toUpperCase() || '?';

  return (
    <div className="flex items-center gap-1.5">
      <span
        title={email}
        className="bg-brand-gradient grid size-8 place-items-center rounded-full text-xs font-semibold text-white ring-2 ring-canvas"
      >
        {initial}
      </span>
      <Button variant="ghost" size="icon-sm" onClick={signOut} aria-label="Sign out" title="Sign out">
        <LogOut />
      </Button>
    </div>
  );
};

interface AppShellProps {
  children: React.ReactNode;
  /** Page-specific controls shown in the header, e.g. the clock in/out button. */
  actions?: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children, actions }) => (
  <div className="flex min-h-screen flex-col">
    <header className="sticky top-0 z-40 border-b border-line bg-canvas/75 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl 2xl:max-w-[1600px] items-center gap-4 px-4 sm:px-6">
        <Link to="/" className="rounded-lg transition-opacity hover:opacity-85" aria-label="Waer home">
          <Logo markClassName="size-7" />
        </Link>

        <MainNav className="ml-4 hidden md:flex" />

        <div className="ml-auto flex items-center gap-2">
          {actions}
          <span className="mx-1 hidden h-6 w-px bg-line-strong sm:block" />
          <UserMenu />
        </div>
      </div>
      <div className="px-4 pb-3 md:hidden">
        <MainNav />
      </div>
    </header>

    <main className="mx-auto w-full max-w-7xl 2xl:max-w-[1600px] flex-1 px-4 py-6 sm:px-6 sm:py-8">{children}</main>
  </div>
);
